/**
 * App shell for AutoDieta IA.
 *
 * Wraps the protected content in AuthProvider. Unauthenticated users see the
 * sign-in screen (ProtectedRoute fallback). Authenticated users see ChatPanel;
 * admin users also see the AdminPanel link/section.
 * All users see a "Seguimiento" tab that renders TrackingDashboard (Fase 3).
 *
 * The AuthService + workerUrl are injected so the same component is testable
 * with a fake service (no Firebase, no network). `main.tsx` provides the real
 * Firebase-backed service.
 *
 * AUTHZ NOTE: isAdmin gate in AppShell is defense-in-depth. The Worker enforces
 * the admin claim on every /admin/* request regardless of client-side state.
 */

import { useState } from "react";
import type { AuthService } from "./auth/authService.js";
import { AuthProvider } from "./auth/AuthContext.js";
import { useAuth } from "./auth/AuthContext.js";
import { ProtectedRoute } from "./auth/ProtectedRoute.js";
import { SignInForm } from "./auth/SignInForm.js";
import { ChatPanel } from "./chat/ChatPanel.js";
import { AdminPanel } from "./admin/AdminPanel.js";
import { TrackingDashboard } from "./dashboard/TrackingDashboard.js";
import { useTracking } from "./dashboard/useTracking.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { useNotifications } from "./notifications/useNotifications.js";
import { NotificationBell } from "./notifications/NotificationBell.js";
import { NotificationPanel } from "./notifications/NotificationPanel.js";
import { ProfileContainer } from "./profile/ProfileContainer.js";
import { WelcomeModal } from "./ui/WelcomeModal.js";
import { ProfileCompletionModal } from "./ui/ProfileCompletionModal.js";
import { useProfile } from "./profile/useProfile.js";
import { useLoginPrompts } from "./profile/useLoginPrompts.js";
import { WeeklyPlanContainer } from "./weekly/WeeklyPlanContainer.js";

export interface AppProps {
  service: AuthService;
  workerUrl: string;
}

interface AppShellProps {
  workerUrl: string;
}

type ActiveTab = "chat" | "tracking" | "perfil" | "admin" | "plan-semanal";

// ---------------------------------------------------------------------------
// TrackingContainer — fetches data and passes it to TrackingDashboard
// ---------------------------------------------------------------------------

interface TrackingContainerProps {
  workerUrl: string;
}

function TrackingContainer({ workerUrl }: TrackingContainerProps): JSX.Element {
  const tracking = useTracking(workerUrl);
  const { getIdToken } = useAuth();

  return (
    <TrackingDashboard
      // Evolution section
      {...(tracking.evolutionPoints !== undefined
        ? { evolutionPoints: tracking.evolutionPoints }
        : {})}
      {...(tracking.loading ? { evolutionLoading: true } : {})}
      {...(tracking.error !== undefined ? { evolutionError: tracking.error } : {})}
      // Adherence section
      {...(tracking.adherence !== undefined ? { adherenceResult: tracking.adherence } : {})}
      {...(tracking.loading ? { adherenceLoading: true } : {})}
      {...(tracking.error !== undefined ? { adherenceError: tracking.error } : {})}
      // Plan section
      {...(tracking.activePlan !== undefined ? { activePlan: tracking.activePlan } : {})}
      {...(tracking.loading ? { planLoading: true } : {})}
      {...(tracking.error !== undefined ? { planError: tracking.error } : {})}
      // Pending proposal
      {...(tracking.pendingProposal !== undefined
        ? { proposal: tracking.pendingProposal }
        : {})}
      // Actions
      onConfirmProposal={tracking.confirm}
      onRejectProposal={tracking.reject}
      // PDF download (plan-pdf-export — enabled when plan exists)
      workerUrl={workerUrl}
      getIdToken={getIdToken}
    />
  );
}

function AppShell({ workerUrl }: AppShellProps): JSX.Element {
  const { user, signOut, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>("chat");
  const [notifOpen, setNotifOpen] = useState(false);
  const notifs = useNotifications(workerUrl);

  // Login prompts orchestration (PR-4 — ADR-4)
  const { profile, loading: profileLoading } = useProfile(workerUrl);
  const navigateToProfile = (): void => setActiveTab("perfil");
  const { showWelcome, showCompletion, missing, dismissWelcome, dismissCompletion } =
    useLoginPrompts({ profile, loading: profileLoading, onNavigateToProfile: navigateToProfile });

  const handleSignOut = async (): Promise<void> => {
    await signOut();
  };

  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Login onboarding modals — rendered above all content, only when authenticated */}
      {user ? (
        <>
          <WelcomeModal
            open={showWelcome}
            onClose={dismissWelcome}
            onGoToProfile={() => {
              navigateToProfile();
              dismissWelcome();
            }}
          />
          <ProfileCompletionModal
            open={showCompletion}
            onClose={dismissCompletion}
            onGoToProfile={() => {
              navigateToProfile();
              dismissCompletion();
            }}
            missing={missing}
          />
        </>
      ) : null}
      {/* App header */}
      <header className="sticky top-0 z-30 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-sm font-black text-white"
          >
            A
          </span>
          <h1 className="flex-1 truncate text-base font-bold tracking-tight">
            AutoDieta IA
          </h1>
          {user ? (
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {isAdmin ? (
                <button
                  type="button"
                  className="min-h-[36px] rounded-lg border border-zinc-700 px-3 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  aria-label={activeTab === "admin" ? "Cerrar panel admin" : "Abrir panel admin"}
                  aria-pressed={activeTab === "admin"}
                  onClick={() => setActiveTab((t) => (t === "admin" ? "chat" : "admin"))}
                >
                  {activeTab === "admin" ? "Chat" : "Admin"}
                </button>
              ) : null}
              <span className="hidden text-xs text-zinc-500 sm:block truncate max-w-[160px]">
                {user.email ?? user.uid}
              </span>
              {/* Notification bell + panel — ADR-2 */}
              <div className="relative">
                <NotificationBell
                  unreadCount={notifs.unreadCount}
                  isOpen={notifOpen}
                  onToggle={() => setNotifOpen((v) => !v)}
                />
                {notifOpen ? (
                  <ErrorBoundary>
                    <NotificationPanel
                      notifications={notifs.notifications}
                      loading={notifs.loading}
                      {...(notifs.error !== undefined ? { error: notifs.error } : {})}
                      onMarkRead={(id) => void notifs.markRead(id)}
                      onClose={() => setNotifOpen(false)}
                      onNavigateToProposal={() => {
                        setActiveTab("tracking");
                        setNotifOpen(false);
                      }}
                    />
                  </ErrorBoundary>
                ) : null}
              </div>
              <button
                type="button"
                className="min-h-[36px] shrink-0 rounded-lg border border-zinc-700 px-3 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                aria-label="Cerrar sesión"
                onClick={() => void handleSignOut()}
              >
                Cerrar sesión
              </button>
            </div>
          ) : null}
        </div>

        {/* Navigation tabs — shown when authenticated */}
        {user ? (
          <nav
            aria-label="Secciones principales"
            className="mx-auto flex max-w-2xl gap-1 border-t border-zinc-800/50 px-4 pb-2 pt-1"
          >
            <TabButton
              label="Chat"
              isActive={activeTab === "chat"}
              onClick={() => setActiveTab("chat")}
            />
            <TabButton
              label="Seguimiento"
              isActive={activeTab === "tracking"}
              onClick={() => setActiveTab("tracking")}
            />
            <TabButton
              label="Perfil"
              isActive={activeTab === "perfil"}
              onClick={() => setActiveTab("perfil")}
            />
            <TabButton
              label="Plan Semanal"
              isActive={activeTab === "plan-semanal"}
              onClick={() => setActiveTab("plan-semanal")}
            />
          </nav>
        ) : null}
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <ProtectedRoute fallback={<SignInForm />}>
          {activeTab === "admin" && isAdmin ? (
            <ErrorBoundary>
              <AdminPanel workerUrl={workerUrl} />
            </ErrorBoundary>
          ) : activeTab === "tracking" ? (
            <ErrorBoundary>
              <TrackingContainer workerUrl={workerUrl} />
            </ErrorBoundary>
          ) : activeTab === "perfil" ? (
            <ErrorBoundary>
              <ProfileContainer workerUrl={workerUrl} />
            </ErrorBoundary>
          ) : activeTab === "plan-semanal" ? (
            <ErrorBoundary>
              <WeeklyPlanContainer workerUrl={workerUrl} />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary>
              <ChatPanel workerUrl={workerUrl} />
            </ErrorBoundary>
          )}
        </ProtectedRoute>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={`min-h-[36px] rounded-lg px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
        isActive
          ? "bg-zinc-800 text-zinc-100"
          : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
      }`}
    >
      {label}
    </button>
  );
}

export function App({ service, workerUrl }: AppProps): JSX.Element {
  return (
    <AuthProvider service={service} workerUrl={workerUrl}>
      <AppShell workerUrl={workerUrl} />
    </AuthProvider>
  );
}
